import { useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import {
  CAP, DAY, EKINDS, EKIND_SHORT, EMAX, EROLES, KINDS, LIMITS, atClock, eColor, eStatus, fmt, fmt2, fmtStamp, hhmm, mean,
  sgn, simulate, toMin, treeOrder, tzOffsetMin, type CapacityTier, type Index, type Kind, type NodeEnergy, type Role, type ShieldKind
} from '@balansa/shared';
import { f2 } from '../lib/geometry';
import { parentPath } from '../lib/helpers';
import { IconBolt } from '../lib/icons';
import { useApp, useWheel } from '../store';
import { Cn } from './Cn';

const EGROUPS: [Role, string][] = [
  ['sleep', 'Miegs — nakts uzlāde'],
  ['food', 'Ēdiens — uzlāde ēdienreizēs'],
  ['charge', 'Dienas uzlāde'],
  ['drain', 'Ikdienas patēriņš'],
  ['shield', 'Noturība']
];
const WD = ['Sv', 'P', 'O', 'T', 'C', 'Pk', 'S'];
/** Līmeņi, ko piedāvā, pievienojot jomu ietilpībai. */
const NEW_TIERS: CapacityTier[] = [
  { min: 0, units: -10 },
  { min: 4, units: 0 },
  { min: 7, units: 10 }
];

const amtTxt = (e: NodeEnergy) =>
  e.role === 'sleep' || e.role === 'food'
    ? e.amount + '% ietilp.'
    : e.role === 'charge'
      ? '+' + e.amount + ' v.'
      : e.role === 'drain'
        ? '−' + e.amount + ' v.'
        : 'līdz −' + e.amount + '%';
const units = (x: number) => (x > 0 ? '+' : x < 0 ? '−' : '') + Math.abs(Math.round(x));

export function EnergyTab() {
  const { wheel, idx, en } = useWheel();
  const { M, L, st, awake, atEnd } = en;
  const E = wheel.energy;
  const A = useApp.getState;
  const [feel, setFeel] = useState<number | null>(null);
  const pct = en.pct;
  const feelV = feel ?? Math.round(pct / 5) * 5;
  const nb = hhmm(awake ? M.T.b : M.T.w);
  const stat = (t: string, v: number, small: string, opts: { sign?: boolean; id?: string } = {}) => (
    <div className="stat" data-testid={opts.id}>
      <span>{t}</span>
      <b className={opts.sign === false ? '' : v >= 0 ? 'pos' : 'neg'}>
        {opts.sign === false ? Math.round(v) : sgn(v)}
        <em> v.</em>
      </b>
      <small>{small}</small>
    </div>
  );
  const cs = wheel.checks.slice(-5);
  const avDiff = mean(cs.map((c) => c.felt - c.model));
  const capAreas = M.capRows.reduce((s, r) => s + r.units, 0);

  return (
    <>
      <div className="ehero">
        <div className="ebig" style={{ '--lv': pct.toFixed(1) + '%', '--lc': eColor(pct) } as CSSProperties}>
          <span className="ebig-fill" />
          <span className="ebig-pct" data-testid="energy-big">
            {Math.round(L)}
          </span>
          {!awake ? (
            <span className="ebig-bolt">
              <IconBolt />
            </span>
          ) : null}
        </div>
        <div className="ehero-t">
          <b>{eStatus(pct)}</b>
          <small data-testid="energy-of">
            {Math.round(L)} no {Math.round(M.cap)} v. ietilpības ({Math.round(pct)}%)
          </small>
          <small>
            {(awake ? 'Nomodā · gulētiešanā (' : 'Lādējas · celoties (') + nb + ') ~' + Math.round(atEnd) + ' v.'}
          </small>
          <small>
            Ilgtermiņā: rītos ~{Math.round(st.morning)}, vakaros ~{Math.round(st.evening)} v.
          </small>
        </div>
      </div>
      <EnergyChart />
      <div id="e-cards" className="stats">
        {stat('Ietilpība', M.cap, 'pamata ' + M.capBase + (capAreas ? ' · jomas ' + units(capAreas) : ''), { sign: false, id: 'stat-cap' })}
        {stat('Miegs (naktī)', M.sleep, fmt(M.T.sleep / 60) + ' h', { id: 'stat-sleep' })}
        {stat('Ēdiens', M.food, M.food ? (M.meals.length ? M.meals.length + ' ēdienreizes' : 'vienmērīgi dienā') : 'nav ēdiena jomas', { id: 'stat-food' })}
        {stat('Patēriņš', -(M.drain - M.day), 't.sk. pamata ' + fmt(M.base) + (M.day ? ' · uzlāde dienā +' + fmt(M.day) : ''), { id: 'stat-drain' })}
        {stat('Aktivitātes', M.act, 'vidēji dienā (7 d.)', { id: 'stat-act' })}
        {stat('Diennakts bilance', M.net, M.net >= 0 ? 'atjaunojas' : 'izsīkst', { id: 'stat-net' })}
      </div>
      <p className="verdict">
        <Verdict />
      </p>

      <h3 className="h3">Kā jūties tieši tagad?</h3>
      <p className="muted">
        Baterija ir aprēķins. Novērtē, cik pilna tā jūtas (no 100%) — baterija turpinās no šī līmeņa, un salīdzinājums ar modeli parādīs, vai
        ietilpība, miegs, ēdiens un aktivitātes ir noregulēti pareizi.
      </p>
      <div className="ecal">
        <input
          type="range"
          className="rng"
          min={0}
          max={100}
          step={5}
          value={feelV}
          aria-label="Mana enerģija tagad"
          style={{ '--p': feelV + '%', '--c': eColor(feelV) } as CSSProperties}
          onChange={(e) => setFeel(+e.target.value)}
        />
        <b>{feelV}%</b>
        <small className="ecal-u">≈ {Math.round((feelV / 100) * M.cap)} v.</small>
        <button
          type="button"
          className="btn sm primary"
          onClick={async () => {
            await A().addCheck(feelV);
            setFeel(null);
          }}
        >
          Saglabāt sajūtu
        </button>
      </div>
      {cs.length ? (
        <>
          <ul className="echecks">
            {cs
              .slice()
              .reverse()
              .map((c) => {
                const df = Math.round(c.felt - c.model);
                return (
                  <li key={c.id}>
                    <span>{fmtStamp(c.ts)}</span>
                    <span>
                      sajūta <b>{c.felt}%</b>
                    </span>
                    <span>modelis {Math.round(c.model)}%</span>
                    <span className={df >= 0 ? 'up' : 'down'}>{(df >= 0 ? '+' : '−') + Math.abs(df)}</span>
                  </li>
                );
              })}
          </ul>
          {cs.length >= 2 && avDiff > 10 ? (
            <p className="muted">Parasti jūties labāk, nekā modelis rēķina — iespējams, ietilpība vai miegs ir novērtēti par zemu, vai aktivitātes maksā mazāk.</p>
          ) : cs.length >= 2 && avDiff < -10 ? (
            <p className="muted">Parasti jūties sliktāk, nekā modelis rēķina — kāda joma vai aktivitāte, iespējams, patērē vairāk, nekā norādīts.</p>
          ) : null}
        </>
      ) : null}

      <h3 className="h3">Ietilpība — cik daudz vari izdarīt</h3>
      <p className="muted">
        Katras jomas apmierinātības līmenis piešķir baterijai ietilpību — arī negatīvu (piem. zema veselība −20 v.). Jo labākā stāvoklī aplis, jo
        lielāka baterija un jo vairāk enerģijas dod miegs un ēdiens, tātad dienā vari atļauties vairāk. Līmenis: “no” jomas vērtības → vienības.
      </p>
      <div className="field eset-wide cap-base">
        <span className="lbl-t">
          Pamata ietilpība — <b data-testid="cap-base">{M.capBase} v.</b> · kopā ar jomām <b data-testid="cap-total">{Math.round(M.cap)} v.</b>
        </span>
        <input
          type="range"
          className="rng"
          min={CAP.baseMin}
          max={CAP.baseMax}
          step={5}
          value={M.capBase}
          aria-label="Pamata ietilpība"
          style={{ '--p': (((M.capBase - CAP.baseMin) / (CAP.baseMax - CAP.baseMin)) * 100).toFixed(1) + '%' } as CSSProperties}
          onChange={(e) => A().setEnergySettings({ capacity: +e.target.value }, true)}
        />
      </div>
      <CapacityRows idx={idx} />

      <h3 className="h3">Miegs, ēdiens un ikdienas plūsmas</h3>
      <p className="muted">
        Galvenie enerģijas avoti ir miegs un ēdiens — to kvalitāti nosaka jomas vērtība uz apļa (0 → 30%, 10 → 100%), daudzumu — ietilpība.
        Ikdienas aktivitātes reģistrē ar kārtīm; šeit — tikai tas, kas notiek katru dienu fonā.
      </p>
      <EnergyRows idx={idx} />

      <h3 className="h3">Diennakts ritms</h3>
      <div className="eset">
        <label className="field">
          <span className="lbl-t">Celšanās</span>
          <TimeInput value={E.wake} onCommit={(v) => A().setEnergySettings({ wake: v })} label="Celšanās" />
        </label>
        <label className="field">
          <span className="lbl-t">Gulētiešana</span>
          <TimeInput value={E.bed} onCommit={(v) => A().setEnergySettings({ bed: v })} label="Gulētiešana" />
        </label>
        <Meals />
        <div className="field eset-wide">
          <span className="lbl-t">
            Nomoda pamatpatēriņš — <b>−{E.base} v.</b> diennaktī
          </span>
          <div className="eset-row">
            <input
              type="range"
              className="rng"
              min={0}
              max={60}
              step={1}
              value={E.base}
              aria-label="Nomoda pamatpatēriņš"
              style={{ '--p': ((E.base / 60) * 100).toFixed(1) + '%' } as CSSProperties}
              onChange={(e) => A().setEnergySettings({ base: +e.target.value }, true)}
            />
            <select className="inp" aria-label="Slodzes veids" value={E.baseKind} onChange={(e) => A().setEnergySettings({ baseKind: e.target.value as Kind })}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {EKINDS[k]} slodze
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <p className="muted" style={{ marginTop: 8 }}>
        Miegs {fmt(M.T.sleep / 60)} h → nakts uzlādes ilguma koeficients {Math.round(M.sleepDur * 100)}% (8 h = 100%). Noturība pašlaik samazina patēriņu:{' '}
        {KINDS.map((k) => EKINDS[k] + ' −' + Math.round((1 - M.remain[k]) * 100) + '%').join(', ')}.
      </p>
      <details className="help">
        <summary>Kā tiek rēķināts</summary>
        <ul>
          <li>
            Baterija mēra enerģiju <b>vienībās (v.)</b>. <b>Ietilpība</b> = pamata ietilpība + jomu līmeņi (katrai jomai darbojas līmenis ar lielāko
            “no”, kas nepārsniedz tās vērtību; virsjomai — apakšjomu vidējais). Robežas 10–300 v.
          </li>
          <li>
            <b>Miegs</b> = ietilpība × jauda% × kvalitāte × ilgums. Kvalitāte nāk no jomas vērtības (0 → 30%, 5 → 65%, 10 → 100%), ilgums no
            diennakts ritma (8 h = 100%).
          </li>
          <li>
            <b>Ēdiens</b> = ietilpība × jauda% × kvalitāte diennaktī, sadalīts vienādās daļās pa ēdienreizēm (ja to nav — vienmērīgi nomodā).
          </li>
          <li>
            <b>Aktivitātes</b> ir kārtis: izspēle maina bateriju uzreiz. Prognozē (līdz gulētiešanai, 7 dienas, ilgtermiņā, bilance) tiek pieskaitīta
            vidējā kāršu enerģija dienā pēdējās 7 dienās.
          </li>
          <li>
            <b>Ikdienas patēriņš</b> pie jomas vērtības 5 ir tieši norādītais; jo zemāka vērtība, jo vairāk joma izsmeļ (0 → ×1,5; 10 → ×0,5).{' '}
            <b>Noturība</b> samazina attiecīgā veida patēriņu (arī kāršu izmaksas) par “līdz %” × vērtība/10, kopā ne vairāk kā par 80%.
          </li>
          <li>
            Baterija mainās no pēdējā zināmā līmeņa (sajūta, kārts, jebkura izmaiņa) robežās 0…ietilpība. Izmaiņas nav retroaktīvas: lielāka
            ietilpība bateriju uzreiz nepiepilda. Laiks tiek rēķināts tavā laika joslā ({wheel.settings.tz}).
          </li>
        </ul>
      </details>
    </>
  );
}

/** Laika lauks, kas saglabā tikai derīgu, pabeigtu vērtību. */
function TimeInput(p: { value: string; onCommit: (v: string) => void; label: string }) {
  const [v, setV] = useState<string | null>(null);
  return (
    <input
      type="time"
      className="inp"
      aria-label={p.label}
      value={v ?? p.value}
      onChange={(e) => {
        setV(e.target.value);
        if (toMin(e.target.value) != null && e.target.value !== p.value) p.onCommit(e.target.value);
      }}
      onBlur={() => setV(null)}
    />
  );
}

/** Ēdienreižu saraksts. */
function Meals() {
  const { wheel, en } = useWheel();
  const A = useApp.getState;
  const meals = wheel.energy.meals;
  const set = (m: string[]) => A().setEnergySettings({ meals: m });
  return (
    <div className="field eset-wide">
      <span className="lbl-t">
        Ēdienreizes{en.M.mealPortion ? <> — katra <b>+{fmt(en.M.mealPortion)} v.</b></> : null}
      </span>
      <div className="meals" data-testid="meals">
        {meals.map((m, i) => (
          <span key={i + m} className="meal">
            <TimeInput
              value={m}
              label={'Ēdienreize ' + (i + 1)}
              onCommit={(v) => set([...meals.slice(0, i), v, ...meals.slice(i + 1)].sort())}
            />
            <button type="button" className="icon-btn" aria-label={'Noņemt ēdienreizi ' + (i + 1)} title="Noņemt" onClick={() => set(meals.filter((_, j) => j !== i))}>
              ×
            </button>
          </span>
        ))}
        {meals.length < LIMITS.meals ? (
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              const last = meals.length ? toMin(meals[meals.length - 1])! : 7 * 60;
              const next = Math.min(last + 180, 23 * 60);
              set([...new Set([...meals, hhmm(next)])].sort());
            }}
          >
            + ēdienreize
          </button>
        ) : null}
      </div>
      {!meals.length ? <small className="muted">Bez ēdienreizēm ēdiens uzlādē vienmērīgi visu nomoda laiku.</small> : null}
      {!en.M.food ? <small className="muted">Ēdiens pašlaik neuzlādē bateriju — piešķir kādai jomai (piem. “Uzturs”) lomu “Ēdiens”.</small> : null}
    </div>
  );
}

/** Jomu ietilpības līmeņi. */
function CapacityRows({ idx }: { idx: Index }) {
  const A = useApp.getState;
  const order = treeOrder(idx);
  const withT = order.filter((id) => idx.node.get(id)!.tiers?.length);
  const free = order.filter((id) => !idx.node.get(id)!.tiers?.length);
  return (
    <>
      <div id="cap-rows" onMouseLeave={() => A().setHover(null, null)}>
        {withT.length ? withT.map((id) => <TierRow key={id} id={id} />) : <p className="empty">Neviena joma vēl neietekmē ietilpību — pievieno zemāk.</p>}
      </div>
      {free.length ? (
        <select
          className="inp e-add"
          aria-label="Pievienot jomu ietilpībai"
          value=""
          onChange={(e) => {
            if (e.target.value) A().setNodeTiers(e.target.value, NEW_TIERS);
          }}
        >
          <option value="">+ Pievienot jomu ietilpībai…</option>
          {free.map((id) => (
            <option key={id} value={id}>
              {'   '.repeat(idx.depth.get(id)!) + idx.node.get(id)!.name}
            </option>
          ))}
        </select>
      ) : null}
    </>
  );
}

function TierRow({ id }: { id: string }) {
  const { idx, en } = useWheel();
  const A = useApp.getState;
  const n = idx.node.get(id)!;
  const v = idx.val.get(id)!;
  const row = en.M.capRows.find((r) => r.id === id);
  const [draft, setDraft] = useState<CapacityTier[] | null>(null);
  const tiers = draft ?? n.tiers;
  const par = parentPath(idx, id);
  const commit = (t: CapacityTier[] = tiers) => {
    setDraft(null);
    if (JSON.stringify(t) !== JSON.stringify(n.tiers)) A().setNodeTiers(id, t);
  };
  const edit = (i: number, p: Partial<CapacityTier>) => setDraft(tiers.map((t, j) => (j === i ? { ...t, ...p } : t)));
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commit();
  };
  const u = row ? row.units : 0;
  // josla 0–10: segmenti pēc līmeņiem (zem pirmā — 0)
  const sorted = [...n.tiers].sort((a, b) => a.min - b.min);
  const segs: { from: number; to: number; units: number }[] = [];
  if (!sorted.length || sorted[0].min > 0) segs.push({ from: 0, to: sorted[0]?.min ?? 10, units: 0 });
  sorted.forEach((t, i) => segs.push({ from: t.min, to: sorted[i + 1]?.min ?? 10, units: t.units }));
  const maxU = Math.max(10, ...sorted.map((t) => Math.abs(t.units)));
  return (
    <div className="erow caprow" data-caprow={n.name} style={{ '--c': idx.color(id) } as CSSProperties} onMouseEnter={() => A().setHover(id, null)}>
      <div className="erow-h">
        <Cn idx={idx} id={id} />
        {par ? <span className="erow-path">{par}</span> : null}
        <span className="sp" />
        <span className="cap-val">vērtība {fmt2(v)}</span>
        <b className={'erow-eff ' + (u > 0 ? 'pos' : u < 0 ? 'neg' : '')} data-testid="cap-units">
          {units(u)} v.
        </b>
        <button type="button" className="icon-btn" title="Noņemt ietilpības līmeņus" aria-label="Noņemt ietilpības līmeņus" onClick={() => A().setNodeTiers(id, [])}>
          ×
        </button>
      </div>
      <div className="tbar" aria-hidden="true">
        {segs.map((s, i) => (
          <span
            key={i}
            className={'tseg ' + (s.units > 0 ? 'pos' : s.units < 0 ? 'neg' : 'zero')}
            style={{ flexGrow: Math.max(s.to - s.from, 0.0001), '--a': (0.35 + (0.65 * Math.abs(s.units)) / maxU).toFixed(2) } as CSSProperties}
          >
            {s.to - s.from >= 1 ? units(s.units) : ''}
          </span>
        ))}
        <i className="tmark" style={{ left: `calc(${(Math.min(10, Math.max(0, v)) * 10).toFixed(1)}% - 1.5px)` }} />
      </div>
      <div className="tiers">
        {tiers.map((t, i) => (
          <span key={i} className={'tier' + (row && row.min === t.min && !draft ? ' on' : '')} data-testid={'tier-' + i}>
            <span>no</span>
            <input
              className="inp num"
              type="number"
              min={0}
              max={10}
              step={0.5}
              aria-label="Līmenis no vērtības"
              value={t.min}
              onChange={(e) => {
                const x = parseFloat(e.target.value.replace(',', '.'));
                if (Number.isFinite(x)) edit(i, { min: x });
              }}
              onBlur={() => commit()}
              onKeyDown={onKey}
            />
            <span>→</span>
            <input
              className="inp num"
              type="number"
              min={-CAP.tierUnits}
              max={CAP.tierUnits}
              step={1}
              aria-label="Ietilpība, v."
              value={t.units}
              onChange={(e) => {
                const x = parseFloat(e.target.value.replace(',', '.'));
                if (Number.isFinite(x)) edit(i, { units: x });
              }}
              onBlur={() => commit()}
              onKeyDown={onKey}
            />
            <button type="button" className="icon-btn" aria-label="Noņemt līmeni" title="Noņemt līmeni" onClick={() => commit(tiers.filter((_, j) => j !== i))}>
              ×
            </button>
          </span>
        ))}
        {tiers.length < LIMITS.tiers ? (
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              const last = tiers.length ? Math.max(...tiers.map((t) => t.min)) : -1;
              commit([...tiers, { min: Math.min(10, last + 1), units: 0 }]);
            }}
          >
            + līmenis
          </button>
        ) : null}
      </div>
    </div>
  );
}

function EnergyRows({ idx }: { idx: Index }) {
  const { wheel, en } = useWheel();
  const A = useApp.getState;
  const order = new Map(treeOrder(idx).map((id, i) => [id, i]));
  const withE = wheel.nodes.filter((n) => n.energy && idx.node.has(n.id));
  const byId = new Map(en.M.rows.map((r) => [r.id, r]));
  const free = treeOrder(idx).filter((id) => !idx.node.get(id)!.energy);

  const groups = EGROUPS.map(([role, title]) => {
    const list = withE.filter((n) => n.energy!.role === role).sort((a, b) => order.get(a.id)! - order.get(b.id)!);
    if (!list.length) return null;
    return (
      <div key={role} className={'egrp e-' + role}>
        <div className="egrp-h">{title}</div>
        {list.map((n) => {
          const e = n.energy!;
          const par = parentPath(idx, n.id);
          const r = byId.get(n.id);
          const eff = !r ? '' : r.role === 'shield' ? '−' + fmt(r.eff) + '% ' + EKIND_SHORT[r.kind as ShieldKind] : sgn(r.eff) + ' v.';
          const kinds: ShieldKind[] = e.role === 'shield' ? [...KINDS, 'all'] : KINDS;
          return (
            <div key={n.id} className="erow" data-erow={n.id} style={{ '--c': idx.color(n.id) } as CSSProperties} onMouseEnter={() => A().setHover(n.id, null)}>
              <div className="erow-h">
                <Cn idx={idx} id={n.id} />
                {par ? <span className="erow-path">{par}</span> : null}
                <span className="sp" />
                <b className={'erow-eff ' + (!r ? '' : r.role === 'shield' ? 'sh' : r.eff >= 0 ? 'pos' : 'neg')}>{eff}</b>
                <button type="button" className="icon-btn" title="Noņemt no enerģijas modeļa" aria-label="Noņemt no enerģijas modeļa" onClick={() => A().setNodeEnergy(n.id, null)}>
                  ×
                </button>
              </div>
              <div className="erow-c">
                <select
                  className="inp"
                  aria-label="Loma"
                  value={e.role}
                  onChange={(ev) => {
                    const role = ev.target.value as Role;
                    A().setNodeEnergy(n.id, { role, amount: Math.min(e.amount, EMAX[role]), kind: role !== 'shield' && e.kind === 'all' ? 'fiz' : e.kind });
                  }}
                >
                  {(Object.keys(EROLES) as Role[]).map((k) => (
                    <option key={k} value={k}>
                      {EROLES[k]}
                    </option>
                  ))}
                </select>
                {e.role === 'drain' || e.role === 'shield' ? (
                  <select className="inp" aria-label="Slodzes veids" value={e.kind} onChange={(ev) => A().setNodeEnergy(n.id, { ...e, kind: ev.target.value as ShieldKind })}>
                    {kinds.map((k) => (
                      <option key={k} value={k}>
                        {k === 'all' ? 'visas slodzes' : EKINDS[k]}
                      </option>
                    ))}
                  </select>
                ) : null}
                <span className="erow-r">
                  <input
                    type="range"
                    className="rng"
                    min={0}
                    max={EMAX[e.role]}
                    step={1}
                    value={e.amount}
                    style={{ '--p': ((e.amount / EMAX[e.role]) * 100).toFixed(1) + '%' } as CSSProperties}
                    aria-label="Lielums"
                    onChange={(ev) => A().setNodeEnergy(n.id, { ...e, amount: +ev.target.value }, true)}
                  />
                  <span className="erow-amt">{amtTxt(e)}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  });

  return (
    <>
      <div id="e-rows" onMouseLeave={() => A().setHover(null, null)}>
        {groups.some(Boolean) ? groups : <p className="empty">Neviena joma vēl nav enerģijas plūsmās — pievieno zemāk (vismaz miegu un ēdienu).</p>}
      </div>
      {free.length ? (
        <select
          className="inp e-add"
          aria-label="Pievienot jomu enerģijas modelim"
          value=""
          onChange={(e) => {
            if (e.target.value) A().setNodeEnergy(e.target.value, { role: 'drain', amount: 10, kind: 'fiz' });
          }}
        >
          <option value="">+ Pievienot jomu plūsmām…</option>
          {free.map((id) => (
            <option key={id} value={id}>
              {'   '.repeat(idx.depth.get(id)!) + idx.node.get(id)!.name}
            </option>
          ))}
        </select>
      ) : null}
    </>
  );
}

function EnergyChart() {
  const { wheel, en } = useWheel();
  const { M, now, L } = en;
  const tz = wheel.settings.tz;
  const cap = M.cap;
  const W = 400;
  const H = 150;
  const pl = 32;
  const pr = 8;
  const pT = 8;
  const pB = 22;
  const span = 7 * DAY;
  const X = (t: number) => pl + ((t - now) / span) * (W - pl - pr);
  const Y = (v: number) => pT + (1 - v / cap) * (H - pT - pB);
  const pts: [number, number][] = [];
  const bands: [number, number][] = [];
  let bs: number | null = null;
  simulate(M, now, L, now + span, {
    every: 3,
    tz,
    typical: true,
    cb: (t, x, a) => {
      pts.push([t, x]);
      if (!a && bs == null) bs = t;
      if (a && bs != null) {
        bands.push([bs, t]);
        bs = null;
      }
    }
  });
  if (bs != null) bands.push([bs, now + span]);
  const d = pts.map((q, i) => (i ? 'L' : 'M') + f2(X(q[0])) + ' ' + f2(Y(q[1]))).join('');
  const noon0 = atClock(now, 720, false, tz);
  const days: ReactNode[] = [];
  for (let i = 0; i <= 8; i++) {
    const t = noon0 + i * DAY;
    if (t < now + 3 * 3600000 || t > now + span - 3 * 3600000) continue;
    const wd = new Date(t + tzOffsetMin(t, tz) * 60000).getUTCDay();
    days.push(
      <text key={i} className="tl" x={f2(X(t))} y={H - 6} textAnchor="middle">
        {WD[wd]}
      </text>
    );
  }
  const grid = [0, Math.round(cap / 2), Math.round(cap)];
  return (
    <div className="echart">
      <h3 className="h3">Prognoze 7 dienām</h3>
      <p className="muted">
        Ja ritms un aktivitātes paliek kā pēdējā nedēļā ({sgn(M.act)} v. dienā). Pelēkās joslas — miegs, sarkanā zona — zem 20% ietilpības.
      </p>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Enerģijas prognoze 7 dienām">
        {bands.map((b, i) => (
          <rect key={i} className="enight" x={f2(X(b[0]))} y={pT} width={f2(X(b[1]) - X(b[0]))} height={H - pT - pB} />
        ))}
        <rect className="elow" x={pl} y={f2(Y(cap * 0.2))} width={W - pl - pr} height={f2(Y(0) - Y(cap * 0.2))} />
        {grid.map((v) => (
          <g key={v}>
            <line className="egrid" x1={pl} x2={W - pr} y1={f2(Y(v))} y2={f2(Y(v))} />
            <text className="tl" x={pl - 5} y={f2(Y(v) + 3)} textAnchor="end">
              {v}
            </text>
          </g>
        ))}
        <path className="earea" d={d + 'L' + f2(X(now + span)) + ' ' + f2(Y(0)) + 'L' + f2(X(now)) + ' ' + f2(Y(0)) + 'Z'} />
        <path className="eline" d={d} />
        {days}
        <circle className="enow" cx={f2(X(now))} cy={f2(Y(L))} r="4.5" />
        <text className="tl" x={f2(X(now) + 8)} y={f2(Math.max(12, Math.min(H - pB - 4, Y(L) - 8)))}>
          tagad
        </text>
      </svg>
    </div>
  );
}

function Verdict() {
  const { idx, en } = useWheel();
  const { M, zeroAt, up, now } = en;
  const spend = M.drain - M.day - M.act;
  const when = (ms: number) => {
    const d = ms / DAY;
    return d < 1 ? 'jau šodien' : Math.round(d) === 1 ? 'pēc ~1 dienas' : 'pēc ~' + Math.round(d) + ' dienām';
  };
  const parts: ReactNode[] = [];
  if (spend >= M.cap)
    parts.push(
      <span key="a">
        <span className="bad">
          Dienas patēriņš ({fmt(spend)} v.) pārsniedz visu ietilpību ({Math.round(M.cap)} v.)
        </span>{' '}
        — pat pēc pilnas uzlādes vakarā būsi tukšs.
      </span>
    );
  else if (M.net >= 0)
    parts.push(
      <span key="a">
        Ritms ir <b>ilgtspējīgs</b>: miegs un ēdiens sedz dienas patēriņu un aktivitātes ({sgn(M.net)} v. diennaktī).
        {zeroAt ? ' Šodien gan līmenis var nokrist līdz 0 — ieplāno atpūtu.' : ''}
      </span>
    );
  else
    parts.push(
      <span key="a">
        Ritms <span className="bad">izsmeļ bateriju</span> ({sgn(M.net)} v. diennaktī).{zeroAt ? ' Baterija sasniegs 0 ' + when(zeroAt - now) + '.' : ''}
      </span>
    );
  const items = M.rows.filter((r) => r.role === 'drain').map((r) => ({ name: idx.node.get(r.id)!.name, v: -r.eff }));
  if (M.base > 0) items.push({ name: 'nomoda pamatpatēriņš', v: M.base });
  if (M.act < 0) items.push({ name: 'aktivitātes (kārtis)', v: -M.act });
  items.sort((a, b) => b.v - a.v);
  if (items.length)
    parts.push(
      <span key="b">
        {' '}
        Lielākais patērētājs: <b>{items[0].name}</b> (−{fmt(items[0].v)} v.).
      </span>
    );
  const worst = [...M.capRows].sort((a, b) => a.units - b.units)[0];
  if (worst && worst.units < 0)
    parts.push(
      <span key="c">
        {' '}
        Ietilpību visvairāk samazina <Cn idx={idx} id={worst.id} /> ({worst.units} v.).
      </span>
    );
  if (!M.food)
    parts.push(
      <span key="f">
        {' '}
        Ēdiens neuzlādē bateriju — piešķir kādai jomai lomu “Ēdiens”.
      </span>
    );
  const best = Array.from(up.entries()).sort((a, b) => b[1] - a[1])[0];
  if (best && best[1] >= 1)
    parts.push(
      <span key="d">
        {' '}
        Vislielāko ieguvumu dotu jomas <Cn idx={idx} id={best[0]} /> uzlabošana — līdz {sgn(best[1])} v. diennaktī.
      </span>
    );
  return <>{parts}</>;
}
